/**
 * The phone shape of primary navigation — the same four entries in the third
 * arrangement, identical to the public site's.
 *
 * Fixed at four items, and that ceiling is exactly why the account
 * destinations live in the account menu instead: adding three of them to a bar
 * already holding four needs seven slots, and no arrangement makes seven fit
 * in four.
 *
 * It carries NO account entry and NO unread dot — the account menu and the
 * notification handle are both in the sticky top bar at this tier, so neither
 * needs a second home down here.
 *
 * The categories entry goes to the standalone browse page rather than opening
 * a disclosure: the tree's host is the navbar, and the navbar is absent at
 * this tier.
 *
 * ALL FOUR ENTRIES ARE ROUTES NOW. المقالات was the last one that left, and it
 * left the same way the catalogue's did: the public host is a separate cookie
 * scope, so the tab it opened held no session and the post it reached could
 * only offer a signed-in reader a sign-in button.
 */
import { NavLink } from 'react-router-dom';

import { chromeCopy } from './copy';
import { IconCategories, IconEditorial, IconHome, IconSearch } from './icons';
import { browseHref } from './nav';
import type { GenreNode } from './GenreTree';

export interface BottomNavProps {
  /** The served vocabulary, which is what resolves the categories entry's
   *  destination. Absent while the read is in flight. */
  readonly categories?: readonly GenreNode[];
}

const ITEM_CLASS =
  'flex min-h-14 flex-col items-center justify-center gap-1 rounded-sm px-1 text-center text-xs text-ink-muted no-underline transition-colors duration-fast hover:bg-hover-bg hover:text-accent-text';

const ACTIVE_CLASS = 'bg-accent-soft font-semibold text-accent-text';

export function BottomNav({ categories }: BottomNavProps = {}) {
  // There is no bare `/genres` route on either host — the tree is the
  // vocabulary's surface — so the entry's destination is the first root term's
  // own listing. While the vocabulary is empty the entry is DROPPED rather than
  // drawn as a link to nowhere; `grid-flow-col` shares the row evenly among
  // whatever is left.
  const categoriesHref = browseHref(categories);

  return (
    <nav
      aria-label={chromeCopy.bottomNavLabel}
      className="fixed inset-x-0 bottom-0 z-[60] grid grid-flow-col gap-1 border-t border-border bg-[color-mix(in_srgb,var(--surface)_96%,transparent)] px-2 pb-[calc(var(--space-2)+env(safe-area-inset-bottom,0px))] pt-2 backdrop-blur-[14px] md:hidden"
    >
      <NavLink
        to="/"
        end
        className={({ isActive }) => (isActive ? `${ITEM_CLASS} ${ACTIVE_CLASS}` : ITEM_CLASS)}
      >
        <IconHome size={20} />
        {chromeCopy.nav.home}
      </NavLink>

      {categoriesHref ? (
        <NavLink
          to={categoriesHref}
          className={({ isActive }) => (isActive ? `${ITEM_CLASS} ${ACTIVE_CLASS}` : ITEM_CLASS)}
        >
          <IconCategories size={20} />
          {chromeCopy.nav.categories}
        </NavLink>
      ) : null}

      <NavLink
        to="/search"
        className={({ isActive }) => (isActive ? `${ITEM_CLASS} ${ACTIVE_CLASS}` : ITEM_CLASS)}
      >
        <IconSearch size={20} />
        {chromeCopy.nav.search}
      </NavLink>

      <NavLink
        to="/blog"
        className={({ isActive }) => (isActive ? `${ITEM_CLASS} ${ACTIVE_CLASS}` : ITEM_CLASS)}
      >
        <IconEditorial size={20} />
        {chromeCopy.nav.editorial}
      </NavLink>
    </nav>
  );
}
